import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface PayPalError {
  message: string;
  details?: any;
}

export const useSubscriptionCreation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [paypalError, setPaypalError] = useState<PayPalError | null>(null);
  const [hasCredentialError, setHasCredentialError] = useState(false);
  
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const resetCreationState = useCallback(() => {
    setIsLoading(false);
    setError(null);
    setPaypalError(null);
    setHasCredentialError(false);
  }, []);

  const handleSubscribe = async (isSubscribed: boolean) => {
    if (!user) {
      toast({
        title: "Login Required",
        description: "Please log in to subscribe to a plan",
        variant: "destructive",
      });
      navigate('/auth');
      return;
    }

    // If the user is already subscribed, prevent creating a new subscription
    if (isSubscribed) {
      toast({
        title: "Already Subscribed",
        description: "You already have an active Pro subscription",
        variant: "default",
      });
      return;
    }

    setIsLoading(true);
    setError(null);
    setPaypalError(null);
    setHasCredentialError(false);

    try {
      console.log('Invoking create-paypal-subscription with userId:', user.id);
      
      const { data, error } = await supabase.functions.invoke('create-paypal-subscription', {
        body: { userId: user.id }
      });

      console.log('Edge function response:', data);

      if (error) {
        console.error('Edge function error:', error);
        
        // Check for credential errors
        if (error.message?.includes('not configured')) {
          setHasCredentialError(true);
        } else {
          setError(new Error(error.message || 'Failed to create subscription'));
          toast({
            title: "Subscription Error",
            description: error.message || "Failed to create subscription",
            variant: "destructive",
          });
        }
        setIsLoading(false);
        return;
      }

      // Check for specific errors in the data response
      if (data?.error) {
        console.error('PayPal API error:', data.error);
        
        // Handle specific PayPal errors
        if (data.error === 'PayPal integration not configured properly' || 
            data.error === 'PayPal authentication failed' ||
            data.error === 'PayPal plan ID is not configured') {
          setHasCredentialError(true);
          toast({
            title: "Configuration Error",
            description: "PayPal integration is not configured properly",
            variant: "destructive",
          });
        } else {
          setPaypalError({
            message: data.error,
            details: data.details
          });
          toast({
            title: "PayPal Error",
            description: data.error || "There was an error with PayPal",
            variant: "destructive",
          });
        }
        setIsLoading(false);
        return;
      }

      // Check for subscription object and links array
      if (data?.subscription?.links) {
        const links = data.subscription.links;
        const approvalLink = links.find((link: any) => link.rel === 'approve');
        
        if (approvalLink) {
          console.log('Redirecting to PayPal approval URL:', approvalLink.href);
          // Keep loading state active during redirection
          window.location.href = approvalLink.href;
          return; // Don't reset loading state as we're redirecting
        } else {
          console.error('No approval link found in PayPal response:', data);
          setError(new Error('No approval link found in PayPal response'));
          toast({
            title: "Subscription Error",
            description: "No approval link found in PayPal response",
            variant: "destructive",
          });
        }
      } else if (data?.links) {
        // For backward compatibility with previous response format
        const approvalLink = data.links.find((link: any) => link.rel === 'approve');
        if (approvalLink) {
          console.log('Redirecting to PayPal approval URL:', approvalLink.href);
          // Keep loading state active during redirection
          window.location.href = approvalLink.href;
          return; // Don't reset loading state as we're redirecting
        } else {
          console.error('No approval link found in PayPal response:', data);
          setError(new Error('No approval link found in PayPal response'));
          toast({
            title: "Subscription Error",
            description: "No approval link found in PayPal response",
            variant: "destructive",
          });
        }
      } else {
        console.error('Invalid PayPal response - missing links array:', data);
        setError(new Error('Invalid PayPal response'));
        toast({
          title: "Subscription Error",
          description: "Invalid response from PayPal",
          variant: "destructive",
        });
      }
    } catch (err: any) {
      console.error('Subscription error:', err);
      setError(err);
      toast({
        title: "Subscription Error",
        description: err.message || "There was an error processing your subscription",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    paypalError,
    hasCredentialError,
    handleSubscribe,
    resetCreationState
  };
};
